import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChildren,
  Directive,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({ selector: '[ctrChipOption]', standalone: true })
export class OptionChipDirective {
  @Input() value: string | undefined;

  public template = inject(TemplateRef<unknown>);
}

@Component({
  selector: 'ctr-select-chip-input',
  imports: [CommonModule, FormsModule],
  templateUrl: './select-chip-input.component.html',
  styleUrl: './select-chip-input.component.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectChipInputComponent), multi: true }],
})
export class SelectChipInputComponent implements ControlValueAccessor {
  /**
   * The options to be displayed.
   */
  @ContentChildren(OptionChipDirective) options: QueryList<OptionChipDirective> | undefined;

  /**
   * The type of files.
   */
  @Input() accept?: string;

  /**
   * The label text to be displayed.
   */
  @Input() label?: string;

  /**
   * the style it will have.
   */

  @Output() selectOption = new EventEmitter<string>();

  /**
   * The value of the input.
   */
  inputValue: string | File = '';

  /**
   * Indicates whether the input is disabled or not.
   */
  isDisabled = false;

  private onChange?: (value: string) => void;
  private onTouched?: () => void;

  /**
   * Writes a new value to the input.
   * @param obj The new value to be set.
   */
  writeValue(obj?: string): void {
    if (obj) {
      this.inputValue = obj;
    }
  }

  /**
   * Registers a callback function to be called when the input value changes.
   * @param fn The callback function to be registered.
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function to be called when the input is touched.
   * @param fn The callback function to be registered.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the input.
   * @param isDisabled Indicates whether the input should be disabled or not.
   */
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Handles the blur event of the input component.
   */
  onBlur() {
    this.onTouched?.();
  }

  changeValue(value: string) {
    this.onChange?.(value);
    this.selectOption.emit(value);
  }
}
